import { useEffect, useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar, SegmentedFilter } from "@/components/ui/Toolbar";
import { ErrorState } from "@/components/ui/States";
import { Icon } from "@/components/ui/Icon";
import { printFile } from "@/lib/api/files";
import { useItem, useList } from "@/lib/api/queries";
import { useSession } from "@/lib/auth/useSession";
import { PERM } from "@/lib/auth/rbac";
import { dateTime, qty, relative, shortId, text } from "@/lib/format";
import { statusLabel } from "@/lib/labels";
import { LpLabels } from "./LpLabels";
import "./lp.css";

/**
 * Palletlar va tara (LP).
 *
 * LP — pallet, tara va korobka uchun BITTA tushuncha: ular bir xil
 * hayot yo'lini bosib o'tadi (yorliq → qabul → joy → yopilish) va
 * ularni alohida ekranlarga ajratish o'sha yo'lning uch nusxasini
 * paydo qilardi. Farq faqat turda, shuning uchun u — filtr.
 *
 * Bu yerda LP YARATILMAYDI va ko'chirilmaydi: qoldiq TSD da
 * skanerlanganda o'zgaradi. Paneldan bajariladigan yagona amal —
 * yorliq chiqarish (bo'sh LP) va qayta chop etish.
 */

interface LpContent {
  sku_id: string;
  lot_ref?: string;
  qty: string;
}

interface LicensePlate {
  id: string;
  lp_code: string;
  lp_type: string;
  status: string;
  current_location_id?: string;
  current_holder_id?: string;
  virtual_sector?: string;
  warehouse_code?: string;
  weight_kg?: string;
  length_cm?: string;
  width_cm?: string;
  height_cm?: string;
  contents: LpContent[];
  has_stock: boolean;
  closed_at?: string;
  version: number;
  created_at: string;
  updated_at: string;
}

interface Movement {
  id: string;
  movement_type: string;
  sku_id: string;
  qty: string;
  source_location_id?: string;
  target_location_id?: string;
  source_holder_id?: string;
  target_holder_id?: string;
  task_id?: string;
  reason: string;
  occurred_at: string;
}

interface Sku {
  id: string;
  article: string;
  name: string;
  base_uom?: string;
}

interface Location {
  id: string;
  code: string;
}

type StatusFilter = "" | "EMPTY" | "RECEIVING" | "OX" | "MX" | "WITH_EMPLOYEE" | "CLOSED";
type TypeFilter = "" | "PALLET" | "CONTAINER" | "BOX";

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "", label: "Hammasi" },
  { value: "EMPTY", label: "Bo'sh" },
  { value: "RECEIVING", label: "Qabulda" },
  { value: "MX", label: "MX da" },
  { value: "OX", label: "OX da" },
  { value: "WITH_EMPLOYEE", label: "Xodimda" },
  { value: "CLOSED", label: "Yopilgan" },
];

/** Tarkib yig'indisi — jadvalda bitta katakka sig'adigan xulosa. */
function summarize(contents: LpContent[]): string {
  if (contents.length === 0) return "bo'sh";
  const total = contents.reduce((sum, row) => sum + Number(row.qty || 0), 0);
  return `${contents.length} xil · ${qty(String(total))}`;
}

/**
 * SKU va manzil nomlarini BITTA so'rovda oladi.
 *
 * LP tarkibida faqat `sku_id` keladi va har bir qator uchun
 * alohida so'rov yuborilsa, yigirma qatorli pallet yigirmata
 * so'rov degani bo'lardi (NFR-002).
 */
function useNames(skuIds: string[], locationIds: string[]) {
  const skuKey = [...new Set(skuIds)].sort().join(",");
  const locationKey = [...new Set(locationIds)].sort().join(",");

  const skus = useList<Sku>(
    "masterData",
    "/api/v1/skus",
    { ids: skuKey, limit: 200 },
    { enabled: skuKey !== "" },
  );
  const locations = useList<Location>(
    "topology",
    "/api/v1/locations",
    { ids: locationKey, limit: 200 },
    { enabled: locationKey !== "" },
  );

  return useMemo(
    () => ({
      sku: new Map((skus.data?.items ?? []).map((row) => [row.id, row])),
      location: new Map((locations.data?.items ?? []).map((row) => [row.id, row.code])),
    }),
    [skus.data, locations.data],
  );
}

export function LicensePlates() {
  const session = useSession();
  const [status, setStatus] = useState<StatusFilter>("");
  const [type, setType] = useState<TypeFilter>("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<LicensePlate | null>(null);
  const [labels, setLabels] = useState(false);
  // Tanlab chop etish: kalit — LP id. Tanlov filtr/qidiruv
  // o'zgarganda ham saqlanadi — bir nechta qidiruvdan yig'ish mumkin.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const canPrint = session.can(PERM.inventoryMove);

  function printSelected() {
    setPrinting(true);
    setPrintError(null);
    printFile("inventory", "/api/v1/license-plates/labels", `lp-yorliqlar-${selected.size}.pdf`, {
      lp_ids: [...selected],
      reason: "Tanlab qayta chop etish (admin)",
    })
      .then(() => setSelected(new Set()))
      .catch((cause: unknown) => setPrintError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setPrinting(false));
  }

  // Qidiruv SERVERGA boradi: LP kodlari ketma-ket beriladi va
  // omborda ularning soni minglab bo'ladi — faqat yuklangan
  // sahifani filtrlash kodni topa olmasdi.
  const query = useList<LicensePlate>("inventory", "/api/v1/license-plates", {
    status: status || undefined,
    lp_type: type || undefined,
    code: search.trim() || undefined,
    limit: 100,
  });

  const rows = query.data?.items ?? [];
  const names = useNames(
    rows.flatMap((row) => row.contents.map((content) => content.sku_id)),
    rows.map((row) => row.current_location_id ?? "").filter(Boolean),
  );

  const columns: Array<Column<LicensePlate>> = [
    {
      key: "code",
      header: "Kod",
      render: (row) => <span className="mono">{row.lp_code}</span>,
    },
    { key: "type", header: "Turi", render: (row) => <StatusBadge value={row.lp_type} tone="tag" /> },
    { key: "status", header: "Holat", render: (row) => <StatusBadge value={row.status} /> },
    {
      key: "where",
      header: "Qayerda",
      // Joy YOKI xodim: LP bir vaqtning o'zida ikkalasida
      // bo'lolmaydi (lp_lifecycle), shuning uchun bitta ustun.
      render: (row) =>
        row.current_location_id ? (
          <span className="mono">
            {names.location.get(row.current_location_id) ?? shortId(row.current_location_id)}
          </span>
        ) : row.current_holder_id ? (
          <span title={row.current_holder_id}>xodim qo'lida</span>
        ) : (
          text(row.virtual_sector)
        ),
    },
    { key: "contents", header: "Tarkibi", render: (row) => summarize(row.contents) },
    {
      key: "updated",
      header: "O'zgargan",
      render: (row) => <span title={dateTime(row.updated_at)}>{relative(row.updated_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      width: "96px",
      render: (row) => (
        <button type="button" className="btn btn-outlined btn-sm" onClick={() => setOpen(row)}>
          <Icon name="search" size={14} />
          Ko'rish
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Palletlar va tara"
        hint="LP — pallet, tara va korobka. Yorliq shu yerdan chiqariladi; tarkib va joy TSD da skanerlanganda o'zgaradi."
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="LP kodi (boshi yetarli)…"
        onRefresh={() => void query.refetch()}
        refreshing={query.isFetching}
      >
        <SegmentedFilter value={status} options={STATUS_OPTIONS} onChange={setStatus} />
        <select
          className="input lp-type-filter"
          value={type}
          onChange={(event) => setType(event.target.value as TypeFilter)}
          aria-label="Turi"
        >
          <option value="">Barcha tur</option>
          <option value="PALLET">Pallet</option>
          <option value="CONTAINER">Tara</option>
          <option value="BOX">Korobka</option>
        </select>
        {canPrint && selected.size > 0 && (
          <>
            <button type="button" className="btn btn-filled" disabled={printing} onClick={printSelected}>
              <Icon name="printer" size={16} />
              {printing ? "Chop etilmoqda…" : `Tanlanganlarni chop etish (${selected.size})`}
            </button>
            <button type="button" className="btn btn-text" onClick={() => setSelected(new Set())}>
              Tanlovni bekor qilish
            </button>
          </>
        )}
        {canPrint && (
          <button type="button" className="btn btn-tonal" onClick={() => setLabels(true)}>
            <Icon name="printer" size={16} />
            Yangi yorliq
          </button>
        )}
      </Toolbar>

      {printError && (
        <div className="notice" role="alert" style={{ marginBottom: 12 }}>
          <Icon name="alert" size={16} />
          <span>{printError}</span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        hasMore={query.data?.has_more}
        emptyTitle={search ? "Bunday kod topilmadi" : "LP yo'q"}
        emptyHint={
          search
            ? "Kodning boshini tering — to'liq yozish shart emas."
            : "«Yangi yorliq» bilan bo'sh LP yarating."
        }
        tableId="license-plates"
        selection={canPrint ? { selected, onChange: setSelected } : undefined}
      />

      <LpLabels open={labels} onClose={() => setLabels(false)} />
      {open && <LpCard lp={open} onClose={() => setOpen(null)} />}
    </>
  );
}

/** Yig'indi kartochkasi — kartaning tepasidagi qator. */
function LpFact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="lp-fact">
      <span className="lp-fact-label">{label}</span>
      <span className={`lp-fact-value${mono ? " mono" : ""}`}>{value}</span>
    </div>
  );
}

/**
 * LP kartasi: holati, tarkibi va harakatlari.
 *
 * TARKIB va HARAKAT ikkalasi ham ko'rsatiladi va bu ataylab:
 * tarkib — hozir nima turibdi, harakat — nima qo'yilgan va nima
 * olingan. Faqat tarkib ko'rsatilsa, bo'shatilgan pallet «hech
 * narsa bo'lmagan» ko'rinardi.
 */
function LpCard({ lp, onClose }: { lp: LicensePlate; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  // Karta ochilganda tarkib QAYTA o'qiladi: ro'yxatdagi nusxa
  // bir necha daqiqa eski bo'lishi mumkin, karta esa qaror
  // qabul qilinadigan joy.
  const fresh = useItem<LicensePlate>("inventory", `/api/v1/license-plates/${lp.id}`);
  const history = useList<Movement>(
    "inventory",
    `/api/v1/license-plates/${lp.id}/history`,
    { limit: 50 },
  );

  const card = fresh.data ?? lp;
  const moves = history.data?.items ?? [];
  const names = useNames(
    [...card.contents.map((row) => row.sku_id), ...moves.map((row) => row.sku_id)],
    [
      card.current_location_id ?? "",
      ...moves.flatMap((row) => [row.source_location_id ?? "", row.target_location_id ?? ""]),
    ].filter(Boolean),
  );

  const skuName = (id: string) => {
    const sku = names.sku.get(id);
    return sku ? `${sku.article} · ${sku.name}` : shortId(id);
  };
  const place = (id?: string) => (id ? (names.location.get(id) ?? shortId(id)) : "—");

  const size = [card.length_cm, card.width_cm, card.height_cm].every((value) => Number(value) > 0)
    ? `${qty(card.length_cm)}×${qty(card.width_cm)}×${qty(card.height_cm)} sm`
    : "—";

  return (
    <dialog ref={dialogRef} className="dialog dialog-wide dialog-card glass-thick" onCancel={onClose}>
      <div className="dialog-head">
        <div>
          <div className="dialog-title mono">{card.lp_code}</div>
          <div className="dialog-desc">
            {statusLabel(card.lp_type)} · {statusLabel(card.status)}
            {card.warehouse_code ? ` · ${card.warehouse_code} korpus` : ""}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outlined btn-sm"
          style={{ marginLeft: "auto" }}
          disabled={printing}
          onClick={() => {
            setPrinting(true);
            setPrintError(null);
            // Qayta chop etish JURNALGA tushadi (nusxa raqami
            // bilan): omborda bir xil kodli ikki yorliq aylanib
            // yurganda qaysi biri keyin chiqarilgani kerak
            // bo'ladi.
            printFile("inventory", "/api/v1/license-plates/labels", `lp-${card.lp_code}.pdf`, {
              lp_ids: [card.id],
              reason: "Qayta chop (panel)",
            })
              .catch((cause: unknown) =>
                setPrintError(cause instanceof Error ? cause.message : String(cause)),
              )
              .finally(() => setPrinting(false));
          }}
        >
          <Icon name="printer" size={14} />
          {printing ? "Chop etilmoqda…" : "Yorliqni qayta chop etish"}
        </button>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="dialog-body">
        {printError && (
          <div className="notice" role="alert">
            <Icon name="alert" size={16} />
            <span>{printError}</span>
          </div>
        )}

        <div className="lp-facts">
          <LpFact label="Holati" value={statusLabel(card.status)} />
          <LpFact
            label="Qayerda"
            mono
            value={
              card.current_location_id
                ? place(card.current_location_id)
                : card.current_holder_id
                  ? "xodim qo'lida"
                  : text(card.virtual_sector)
            }
          />
          <LpFact label="O'lchami" value={size} />
          <LpFact label="Vazni" value={Number(card.weight_kg) > 0 ? `${qty(card.weight_kg)} kg` : "—"} />
        </div>

        {card.status === "CLOSED" && (
          <div className="notice" role="status">
            <Icon name="alert" size={16} />
            <span>
              LP yopilgan{card.closed_at ? ` — ${dateTime(card.closed_at)}` : ""}. Kod qayta
              ishlatilmaydi: eski yorliq yangi palletga tegishli bo'lib qolmasligi kerak.
            </span>
          </div>
        )}

        <div className="lp-section">Hozirgi tarkibi</div>
        {fresh.isLoading ? (
          <div className="skeleton" style={{ height: 72 }} />
        ) : fresh.error ? (
          <ErrorState error={fresh.error} onRetry={() => void fresh.refetch()} />
        ) : card.contents.length === 0 ? (
          <p className="field-hint" style={{ margin: 0 }}>
            Bo'sh — qoldiq yo'q.
          </p>
        ) : (
          <table className="data" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Tovar</th>
                <th>Partiya</th>
                <th style={{ textAlign: "right" }}>Miqdor</th>
              </tr>
            </thead>
            <tbody>
              {card.contents.map((row) => (
                <tr key={`${row.sku_id}-${row.lot_ref ?? ""}`}>
                  <td>{skuName(row.sku_id)}</td>
                  <td className="mono">{text(row.lot_ref)}</td>
                  <td style={{ textAlign: "right" }}>{qty(row.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="lp-section">Harakatlar — nima qo'yilgan va nima olingan</div>
        {history.isLoading ? (
          <div className="skeleton" style={{ height: 96 }} />
        ) : history.error ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} />
        ) : moves.length === 0 ? (
          <p className="field-hint" style={{ margin: 0 }}>
            Harakat yo'q — LP hali ishlatilmagan.
          </p>
        ) : (
          <table className="data" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Qachon</th>
                <th>Amal</th>
                <th>Tovar</th>
                <th style={{ textAlign: "right" }}>Miqdor</th>
                <th>Qayerdan → qayerga</th>
              </tr>
            </thead>
            <tbody>
              {moves.map((row) => (
                <tr key={row.id}>
                  <td title={dateTime(row.occurred_at)}>{relative(row.occurred_at)}</td>
                  <td>
                    <StatusBadge value={row.movement_type} tone="tag" />
                  </td>
                  <td>{skuName(row.sku_id)}</td>
                  <td style={{ textAlign: "right" }}>{qty(row.qty)}</td>
                  <td className="mono">
                    {place(row.source_location_id)} → {place(row.target_location_id)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </dialog>
  );
}
